'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgChevronleft, SvgChevronright, SvgStarFill } from '@common/icon';
import { cn } from '@core/utils';
import type { ReviewPreviewItem } from '@generated/unauth';
import { useQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { UnauthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { MarketReviewDetailContent, type MarketReviewItem } from '../../../_components/MarketReviewDetailContent';
interface ReviewPreviewProps {
  productId: string;
}
const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
};
const ReviewCard = t0 => {
  const $ = _c(32);
  const {
    review,
    isMobile,
    onClick
  } = t0;
  const t1 = isMobile ? "w-232 py-16" : "w-300 py-20";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex shrink-0 cursor-pointer gap-12", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onClick) {
    t3 = e => {
      if (e.key === "Enter" || e.key === " ") {
        onClick?.();
      }
    };
    $[2] = onClick;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== review.thumbnailUrl) {
    t4 = review.thumbnailUrl && <div className="relative size-88 shrink-0 overflow-hidden"><Image fill={true} sizes="88px" src={review.thumbnailUrl} alt={"\uB9AC\uBDF0 \uC774\uBBF8\uC9C0"} className="object-cover" /></div>;
    $[4] = review.thumbnailUrl;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== review.nickname) {
    t5 = <p className="truncate font-400 text-Gray-600 typo-body4">{review.nickname}</p>;
    $[6] = review.nickname;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SvgStarFill className="size-16 text-Y-550" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== review.rating) {
    t7 = <div className="flex items-center gap-2">{t6}<span className="font-600 text-Gray-600 typo-caption1">{review.rating}</span></div>;
    $[9] = review.rating;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== review.createdAt) {
    t8 = formatDate(review.createdAt);
    $[11] = review.createdAt;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <span className="font-400 text-Gray-600 typo-body4">{t8}</span>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <div className="flex items-center gap-4">{t7}{t9}</div>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t5) {
    t11 = <div className="flex flex-col">{t5}{t10}</div>;
    $[18] = t10;
    $[19] = t5;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== review.contents) {
    t12 = <p className="line-clamp-2 font-400 text-Gray-900 typo-body4">{review.contents}</p>;
    $[21] = review.contents;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t12) {
    t13 = <div className="flex min-w-0 flex-col gap-4">{t11}{t12}</div>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== onClick || $[27] !== t13 || $[28] !== t2 || $[29] !== t3 || $[30] !== t4) {
    t14 = <div role="button" tabIndex={0} className={t2} onClick={onClick} onKeyDown={t3}>{t4}{t13}</div>;
    $[26] = onClick;
    $[27] = t13;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
export const ReviewPreview = t0 => {
  const $ = _c(78);
  const {
    productId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start",
      slidesToScroll: 3,
      containScroll: "trimSnaps"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [emblaRefMobile] = useEmblaCarousel(t2);
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [scrollSnaps, setScrollSnaps] = useState(t3);
  let t4;
  if ($[3] !== emblaApi) {
    t4 = () => {
      if (!emblaApi) {
        return;
      }
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    $[3] = emblaApi;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const onSelect = t4;
  let t5;
  let t6;
  if ($[5] !== emblaApi || $[6] !== onSelect) {
    t5 = () => {
      if (!emblaApi) {
        return;
      }
      setScrollSnaps(emblaApi.scrollSnapList());
      onSelect();
      emblaApi.on("select", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
      };
    };
    t6 = [emblaApi, onSelect];
    $[5] = emblaApi;
    $[6] = onSelect;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== productId) {
    t7 = UnauthQueryOptions["get:/wello/v2/allows/local-market/gift-review-feed/{productId}"]({
      path: {
        productId
      },
      query: {
        previewSize: 10
      }
    });
    $[9] = productId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    data
  } = useQuery(t7);
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t8;
  let t9;
  if ($[11] !== data?.context?.reviews || $[12] !== data?.context?.satisfaction || $[13] !== emblaApi || $[14] !== emblaRef || $[15] !== emblaRefMobile || $[16] !== scrollSnaps || $[17] !== selectedIndex) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const reviews = data?.context?.reviews ?? [];
      const satisfaction = data?.context?.satisfaction;
      if (reviews.length === 0) {
        t16 = null;
        break bb0;
      }
      const satisfactionRate = satisfaction?.satisfactionRate ?? 0;
      const reviewItems = reviews.map(_temp);
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = cn("flex flex-col gap-12", "mobile-tablet:gap-4 mobile-tablet:px-20");
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = <div className="mb-16 flex items-center justify-center bg-O-25 p-20 text-center font-500 text-O-400 typo-body4 mobile-tablet:mb-28"><div className="w-full"><p>이 상품은 고향사랑기부 답례품으로도 제공돼요.</p><p>기부를 통해 먼저 받아보신 분들의 솔직한 후기를 확인해보세요.</p></div></div>;
        $[28] = t13;
      } else {
        t13 = $[28];
      }
      let t17;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = cn("font-600 text-Gray-900 typo-h6", "mobile-tablet:text-Gray-black mobile-tablet:typo-body2");
        $[29] = t17;
      } else {
        t17 = $[29];
      }
      let t18;
      if ($[30] !== satisfactionRate) {
        t18 = <h3 className={t17}>기부자 <span className="text-O-400">{satisfactionRate}%</span>가 만족한 상품</h3>;
        $[30] = satisfactionRate;
        $[31] = t18;
      } else {
        t18 = $[31];
      }
      let t19;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = cn("flex items-center gap-8", "mobile-tablet:hidden");
        $[32] = t19;
      } else {
        t19 = $[32];
      }
      const t20 = selectedIndex + 1;
      let t21;
      if ($[33] !== t20) {
        t21 = <span className="font-700 text-Gray-900">{t20} </span>;
        $[33] = t20;
        $[34] = t21;
      } else {
        t21 = $[34];
      }
      let t22;
      if ($[35] !== scrollSnaps.length) {
        t22 = <span className="text-Gray-500">/ {scrollSnaps.length}</span>;
        $[35] = scrollSnaps.length;
        $[36] = t22;
      } else {
        t22 = $[36];
      }
      let t23;
      if ($[37] !== t21 || $[38] !== t22) {
        t23 = <p className="typo-body4">{t21}{t22}</p>;
        $[37] = t21;
        $[38] = t22;
        $[39] = t23;
      } else {
        t23 = $[39];
      }
      let t24;
      if ($[40] !== emblaApi) {
        t24 = () => emblaApi?.scrollPrev();
        $[40] = emblaApi;
        $[41] = t24;
      } else {
        t24 = $[41];
      }
      const t25 = selectedIndex === 0;
      let t26;
      if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = <SvgChevronleft className="size-18 text-Gray-600" />;
        $[42] = t26;
      } else {
        t26 = $[42];
      }
      let t27;
      if ($[43] !== t24 || $[44] !== t25) {
        t27 = <button type="button" className="flex size-24 items-center justify-center rounded-xxsmall border border-Gray-300 bg-Gray-white disabled:opacity-50" onClick={t24} disabled={t25}>{t26}</button>;
        $[43] = t24;
        $[44] = t25;
        $[45] = t27;
      } else {
        t27 = $[45];
      }
      let t28;
      if ($[46] !== emblaApi) {
        t28 = () => emblaApi?.scrollNext();
        $[46] = emblaApi;
        $[47] = t28;
      } else {
        t28 = $[47];
      }
      const t29 = selectedIndex === scrollSnaps.length - 1;
      let t30;
      if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = <SvgChevronright className="size-18 text-Gray-600" />;
        $[48] = t30;
      } else {
        t30 = $[48];
      }
      let t31;
      if ($[49] !== t28 || $[50] !== t29) {
        t31 = <button type="button" className="-ml-px flex size-24 items-center justify-center rounded-xxsmall border border-Gray-300 bg-Gray-white disabled:opacity-50" onClick={t28} disabled={t29}>{t30}</button>;
        $[49] = t28;
        $[50] = t29;
        $[51] = t31;
      } else {
        t31 = $[51];
      }
      let t32;
      if ($[52] !== t27 || $[53] !== t31) {
        t32 = <div className="flex">{t27}{t31}</div>;
        $[52] = t27;
        $[53] = t31;
        $[54] = t32;
      } else {
        t32 = $[54];
      }
      let t33;
      if ($[55] !== t23 || $[56] !== t32) {
        t33 = <div className={t19}>{t23}{t32}</div>;
        $[55] = t23;
        $[56] = t32;
        $[57] = t33;
      } else {
        t33 = $[57];
      }
      let t34;
      if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
        t34 = <button type="button" className="hidden items-center gap-4 font-600 text-Gray-600 typo-body4 mobile-tablet:flex">전체보기<SvgChevronright className="size-12 text-Gray-600" /></button>;
        $[58] = t34;
      } else {
        t34 = $[58];
      }
      if ($[59] !== t18 || $[60] !== t33) {
        t14 = <div className="flex items-center justify-between">{t18}{t33}{t34}</div>;
        $[59] = t18;
        $[60] = t33;
        $[61] = t14;
      } else {
        t14 = $[61];
      }
      let t35;
      if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
        t35 = cn("relative overflow-hidden", "mobile-tablet:hidden");
        $[62] = t35;
      } else {
        t35 = $[62];
      }
      let t36;
      if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
        t36 = <div className="pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-Gray-white to-transparent" />;
        $[63] = t36;
      } else {
        t36 = $[63];
      }
      t15 = <div className={t35} ref={emblaRef}><div className="flex gap-20">{reviews.map((review_0, index) => <ReviewCard key={review_0.reviewId} review={review_0} onClick={() => {
            openResponsiveDialog({
              component: MarketReviewDetailContent,
              modalHeader: "\uD6C4\uAE30 \uC0C1\uC138",
              className: {
                modal: "max-w-[900px]"
              },
              props: {
                reviews: reviewItems,
                initialIndex: index
              },
              drawerNoPadding: true
            });
          }} />)}</div>{t36}</div>;
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = cn("hidden", "mobile-tablet:block mobile-tablet:overflow-hidden");
        $[64] = t10;
      } else {
        t10 = $[64];
      }
      t11 = emblaRefMobile;
      t8 = "flex gap-20";
      t9 = reviews.map((review_1, index_0) => <ReviewCard key={review_1.reviewId} review={review_1} isMobile={true} onClick={() => {
        openResponsiveDialog({
          component: MarketReviewDetailContent,
          modalHeader: "\uD6C4\uAE30 \uC0C1\uC138",
          className: {
            modal: "max-w-[900px]"
          },
          props: {
            reviews: reviewItems,
            initialIndex: index_0
          },
          drawerNoPadding: true
        });
      }} />);
    }
    $[11] = data?.context?.reviews;
    $[12] = data?.context?.satisfaction;
    $[13] = emblaApi;
    $[14] = emblaRef;
    $[15] = emblaRefMobile;
    $[16] = scrollSnaps;
    $[17] = selectedIndex;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t8;
    $[26] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[65] !== t8 || $[66] !== t9) {
    t17 = <div className={t8}>{t9}</div>;
    $[65] = t8;
    $[66] = t9;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  let t18;
  if ($[68] !== t10 || $[69] !== t11 || $[70] !== t17) {
    t18 = <div className={t10} ref={t11}>{t17}</div>;
    $[68] = t10;
    $[69] = t11;
    $[70] = t17;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  let t19;
  if ($[72] !== t12 || $[73] !== t13 || $[74] !== t14 || $[75] !== t15 || $[76] !== t18) {
    t19 = <div className={t12}>{t13}{t14}{t15}{t18}</div>;
    $[72] = t12;
    $[73] = t13;
    $[74] = t14;
    $[75] = t15;
    $[76] = t18;
    $[77] = t19;
  } else {
    t19 = $[77];
  }
  return t19;
};
function _temp(review) {
  return {
    rating: review.rating ?? 0,
    nickname: review.nickname ?? "",
    date: formatDate(review.createdAt),
    content: review.contents ?? "",
    imageUrlList: review.thumbnailUrl ? [review.thumbnailUrl] : []
  };
}